import * as Business               from '../business';
import * as Forms                  from '../components/forms';
import * as User                   from '../user';
import { addManyContacts }         from '../contacts/actions';
import { connect }                 from 'react-redux';
import { getFriendlyErrorMessage } from '../util/errors';
import Alert                       from '../components/alert';
import classnames                  from 'classnames';
import guessColumn                 from './guess_column';
import Link                        from '../components/link';
import MapFields                   from './map_fields';
import Navbar                      from '../components/navbar';
import pluralize                   from '../util/pluralize';
import React                       from 'react';
import restrictAccess              from '../components/restrict_access';
import SplitViewOnly               from './split_view_only';
import Swal                        from 'sweetalert2';
import UploadClipboard             from './upload_clipboard';
import UploadFile                  from './upload_file';



const initialState = {
  headers:        null, // [{ value: 'firstname', label: 'First Name'}, {value: 'email', label: 'E-mail'} ],
  allRecords:     null,
  records:        null, // [ [ 'a', 'b@broadly.com' ], [ 'c', 'd@broadly.com' ], [ 'd', 'invalid' ] ],
  mapping:        null,  // {'name.full': 'full_name', id: 'customer_id', ...}
  mappingErrors:  null,
  error:          null,
  summary:        null,
  customers:      null,
  view:           'upload',
  sendThankYou:   true,
  customersReady: 0
};

const validationErrors = {
  emptyRecords: fileName => ({
    type:    'error',
    message: (
      <div>
        We could not find any customer records in your file. {fileName && (<span>Your file: <b>{fileName}</b></span>)}
      </div>
    )
  }),
  missingColumns: fileName => ({
    type:    'error',
    message: (
      <div>
        Please upload a file that has customer name, and email and/or phone number. {fileName && (<span>Your file: <b>{fileName}</b></span>)}
      </div>
    )
  }),
  largeFileWithReviewRequests: {
    type:    'warn',
    message: (
      <div>
        You can send up to 500 review requests at a time.<br/><br/>
        If your list has over 500 contacts, you may be reaching too far back in time to be effective.  <Link href="https://broadlyhelp.force.com/s/article/what-are-some-best-practices-to-maximize-my-response-rate" external>View our best practices for maximizing your response rate.</Link>
      </div>
    )
  },
  largeFileWithNoReviewRequests: {
    type:    'warn',
    message: (
      <div>
        You can import up to 5,000 contacts at a time.
      </div>
    )
  },
  notSupported: ({ message, file }) => ({
    type:    'error',
    message: (
      <div>{message} {file && (<span>Your file: <b>{file.name}</b></span>)}</div>
    )
  })
};

class AddFilePage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = initialState;

    this.handleParseSuccess  = this.handleParseSuccess.bind(this);
    this.handleParseError    = this.handleParseError.bind(this);
    this.handleMappingChange = this.handleMappingChange.bind(this);
    this.handleCancel        = this.handleCancel.bind(this);
    this.handleSubmit        = this.handleSubmit.bind(this);
    this.handleOnPaste       = this.handleOnPaste.bind(this);
    this.setSendThankYou     = this.setSendThankYou.bind(this);
  }

  handleParseSuccess({ headers, records, fileName }) {
    const haveHeaders       = headers && headers.length;
    const haveNeededHeaders = haveHeaders && headers.length >= 2;
    const haveRecords       = records && records.length > 0;
    const canMap            = haveRecords && haveNeededHeaders;
    const isEmpty           = !haveRecords || !haveHeaders;
    const onlyHeaders       = !haveRecords;

    if (canMap)
      this.setMappingState({ headers, fileName, allRecords: records, records });
    else if (isEmpty || onlyHeaders)
      this.setState({ error: validationErrors.emptyRecords(fileName) });
    else
      this.setState({ error: validationErrors.missingColumns(fileName) });
  }

  handleParseError(error) {
    this.setState({ error: validationErrors.notSupported(error) });
  }

  setMappingState({ headers, fileName, allRecords, records }) {
    const { provider }     = this.props;
    const { sendThankYou } = this.state;

    const preparedHeaders = headers.map(normalizeHeader);
    const headerAsObject  = headers.map((header, index) => ({
      label: headers[index],
      value: preparedHeaders[index]
    }));

    const { recordsLimited, error } = checkSize({ allRecords, sendThankYou });

    const { initialMapping } = this.props;
    const mapping            = this.state.mapping || getInitialMapping({ headers: preparedHeaders, initialMapping });
    const hasMapping         = mapping && Object.keys(mapping).length > 0;

    const newState = {
      headers:    headerAsObject,
      allRecords: records,
      records:    recordsLimited,
      view:       'mapping',
      fileName,
      error,
      mapping
    };

    if (hasMapping) {
      this.setState({
        ...newState,
        ...getContactsAndSummary({
          records: newState.records,
          headers: newState.headers,
          mapping,
          provider,
          sendThankYou
        })
      });
    } else
      this.setState({ ...newState });
  }

  handleOnPaste(event) {
    event.preventDefault();

    this.uploadFile.current.copyFromClipboard();
  }

  handleMappingChange({ mapping }) {
    const errors = validate(mapping);

    const { allRecords }   = this.state;
    const { headers }      = this.state;
    const { provider }     = this.props;
    const { sendThankYou } = this.state;

    const { recordsLimited, error } = checkSize({ allRecords, sendThankYou });

    this.setState({
      errors,
      mapping,
      records: recordsLimited,
      error,
      ...getContactsAndSummary({ records: recordsLimited, headers, mapping, provider, sendThankYou })
    });
  }

  async handleSubmit(event) {
    event.preventDefault();

    const { customers }        = this.state;
    const validCustomers       = customers.filter(isValidCustomer);
    const { mapping }          = this.state;
    const { addMany }          = this.props;
    const { updateCSVMapping } = this.props;

    try {
      await addMany(validCustomers);
      updateCSVMapping(mapping).catch(); // @TODO send to rollbar?
      this.setState({ ...initialState, mapping });
      Swal.fire({
        text: 'Customers imported!',
        icon: 'success'
      });
    } catch (error) {
      Swal.fire({
        text: getFriendlyErrorMessage(error),
        icon: 'error'
      });
    }
  }

  handleCancel(event) {
    event.preventDefault();

    this.setState({ ...initialState, mapping: this.props.initialMapping });
  }

  setSendThankYou(sendThankYou) {
    const { allRecords } = this.state;
    const { headers }    = this.state;
    const { mapping }    = this.state;
    const { provider }   = this.props;

    const errors = validate(mapping);

    const { recordsLimited, error } = checkSize({ allRecords, sendThankYou });

    this.setState({
      sendThankYou,
      errors,
      mapping,
      error,
      ...getContactsAndSummary({ records: recordsLimited, headers, mapping, provider, sendThankYou })
    });
  }

  render() {
    const { businessName }   = this.props;
    const { customersReady } = this.state;
    const { headers }        = this.state;
    const { records }        = this.state;
    const { mapping }        = this.state;
    const { summary }        = this.state;
    const { mappingErrors }  = this.state;
    const { error }          = this.state;
    const { view }           = this.state;
    const { fileName }       = this.state;
    const { sendThankYou }   = this.state;
    const isUpload           = view === 'upload';
    const isMapping          = view === 'mapping';
    const canImport          = isMapping && customersReady > 0;
    const buttonsClassName   = isUpload ? 'buttons buttons--spaced' : 'buttons';

    return (
      <React.Fragment>
        <Navbar title="Add From File"/>
        <SplitViewOnly>
          <div className="page-content">
            <div className="add-from-file block">
              {businessName && (<div className="add-from-file__heading">IMPORT CUSTOMERS TO {businessName.toUpperCase()}</div>)}
              <div className="add-from-file__description">
                Upload a spreadsheet with your recent customers and we will send them a review request.<br/>
                <p>Need help importing? Please see <a href="https://broadlyhelp.force.com/s/article/add-from-file" target="_blank" rel="noopener noreferrer" className="external">our support center</a>
                &nbsp;or <Link href="https://s3.amazonaws.com/helpscout.net/docs/assets/5aa029082c7d3a7549516d89/attachments/5b6892282c7d3a03f89d671c/Broadly-Upload-Template.csv" external>download sample template</Link>.
                </p>
              </div>
              <div className="add-from-file__error">
                {error && <Alert type={error.type}>{error.message}</Alert>}
              </div>
              {isUpload && <UploadFile onSuccess={this.handleParseSuccess} onError={this.handleParseError}/>}
              {isMapping && (
                <MapFields
                  fileName={fileName}
                  mapping={mapping}
                  headers={headers}
                  records={records}
                  errors={mappingErrors}
                  summary={summary}
                  onMappingChange={this.handleMappingChange}/>
              )}
              {canImport && (
                <SendThankYou sendThankYou={sendThankYou} setSendThankYou={this.setSendThankYou}/>
              )}
              <div className={buttonsClassName}>
                {isUpload  && (<UploadClipboard onSuccess={this.handleParseSuccess} onError={this.handleParseError}/>)}
                {isMapping && (<button className="button button-big" onClick={this.handleCancel}>Cancel</button>)}
                {canImport && (<button className="button button-big button-fill" onClick={this.handleSubmit}>Import {customersReady} {pluralize(customersReady, 'Customer')}</button>)}
                {(isUpload || !canImport) && (<button className="button button-big button-fill" disabled>Import Customers</button>)}
              </div>
            </div>
          </div>
        </SplitViewOnly>
      </React.Fragment>
    );
  }
}


function SendThankYou({ sendThankYou, setSendThankYou }) {
  const shouldSendThankYou = sendThankYou === true;

  return (
    <div className="send-thank-you list block">
      <ul>
        <Forms.Radio
          label="Send review requests"
          name="sendThankYou"
          value="yes"
          subtitle="We will send review requests to all contacts in your file."
          useRadioStyle={true}
          defaultChecked={shouldSendThankYou}
          onChange={() => setSendThankYou(true)}
          className={classnames('send-yes', { 'is-active': shouldSendThankYou })}
        >
        </Forms.Radio>

        <Forms.Radio
          label="Don't send review requests"
          name="sendThankYou"
          value="no"
          subtitle="Upload contacts for messaging, send a review request later from the contact page."
          useRadioStyle={true}
          defaultChecked={!shouldSendThankYou}
          onChange={() => setSendThankYou(false)}
          className={classnames('send-no', { 'is-active': !shouldSendThankYou  })}
        />
      </ul>

    </div>

  );
}


export function normalizeHeader(header) {
  const stripped = header.replace(/[#`;:'",.-]/g, '');
  return stripped.trim().toLowerCase().replace(/\s/g, '_');
}


function validate(mapping) {
  const errors = {};
  if (!mapping.phone && !mapping.email)
    errors.email = 'Phone or Email column is required.  Please match at least one of your columns to phone or email';

  if (!mapping.full && !mapping.given && !mapping.preferred && !mapping.surname)
    errors.full = 'Name is required.  Please match at least one of your columns to fields: full name, first name, or last name';

  if (Object.keys(errors).length > 0)
    return errors;
  else
    return null;
}

function getContactsAndSummary({ headers, mapping, records, provider, sendThankYou }) {
  const customers      = getContacts({ headers, mapping, records, provider, sendThankYou });
  const summary        = getSummary(customers);
  const customersReady = summary.ready;

  return {
    customers,
    summary,
    customersReady
  };
}

function getContacts({ headers, records, mapping, provider, sendThankYou }) {
  const recordsMapped = getRecordsMapped({ headers, mapping, records });
  const customers     = recordsMapped.map(record => toCustomerSchema({ record, provider, sendThankYou }));

  return customers;
}

function getRecordsMapped({ headers, records, mapping }) {
  // headers [{ value: 'full name', label: 'Full Name'}, {value: 'customer id', label: 'Customer ID'}]
  // mapping {name: 'full_name', id: 'customer_id', ...}
  // records [['vinicius', '2']]
  // result  [[name: 'vinicius', id: 2]]
  const mappingInverted = swapKeyWithValue(mapping);

  // mappingInverted {first_name:'given', last_name:'surname', email_address:'email'}
  const mappingAsIndex = headers.reduce((acc, header, index) => {
    acc[index] = mappingInverted[header.value]; // eslint-disable-line

    return acc;
  }, {});
  const recordsMapped = records.map(record => {
    return record.reduce((acc, value, index) => {
      const key = mappingAsIndex[index];
      acc[key]  = value.trim ? value.trim() : value; // eslint-disable-line

      return acc;
    }, {});
  });

  return recordsMapped;
}

function swapKeyWithValue(data) {
  return Object.assign({}, ...Object.entries(data).map(([ a, b ]) => ({ [b]: a })));
}


function isValidCustomer(customer) {
  return hasValidName(customer) &&
    hasValidContact(customer);
}

function hasValidName({ name }) {
  return name.full || name.given || name.preferred || name.surname;
}

function hasValidContact({ contact }) {
  return contact.phone || isValidEmail(contact.email);
}

function isValidEmail(email) {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(email);
}


function toCustomerSchema({ record, provider, sendThankYou }) {
  // Everytime sendThankYou is disabled, update ignoreActivityBefore timestamp.
  // We will ignore all customer activity older than this date to prevent
  // followup messaging.
  const ignoreActivityBefore = sendThankYou ? undefined : new Date();
  const transactions         = getTransactions({ record, sendThankYou });

  const customer = {
    id:   record.id,
    name: {
      full:      record.full,
      given:     record.given,
      surname:   record.surname,
      preferred: record.preferred
    },
    contact: {
      email: record.email,
      phone: record.phone
    },
    // If provider, add to transaction.
    transactions,
    customerSince: record.customerSince,
    ignoreActivityBefore,
    source:        {
      name: 'file'
    },
    provider
  };

  return customer;
}

function getTransactions({ record, sendThankYou }) {
  const timestamp = sendThankYou ?
    record.lastTransaction || new Date() :
    // When we don't want to send a review request, only save last transaction
    // date if its in the record. This is so that we don't schedule messages because
    // the ignoreActivityBefore date is older or the same time as the transaction timestamp.
    record.lastTransaction || null;

  if (timestamp) {
    const provider     = record.provider ? { name: record.provider } : null;
    const transactions = [{ timestamp, provider }];
    return transactions;
  } else
    return [];
}

function checkSize({ allRecords, sendThankYou }) {
  if (sendThankYou) {
    const isLarge        = allRecords.length > 500;
    const recordsLimited = allRecords.slice(0, 500);
    const error          = isLarge ? validationErrors.largeFileWithReviewRequests : null;
    return { recordsLimited, error };
  } else {
    const isLarge        = allRecords.length > 5000;
    const recordsLimited = allRecords.slice(0, 5000);
    const error          = isLarge ? validationErrors.largeFileWithNoReviewRequests : null;
    return { recordsLimited, error };
  }
}

function getInitialMapping({ headers, initialMapping }) {
  const guessed = headers.reduce((acc, header) => {
    const value = guessColumn(header);
    if (value)
      acc[value] = header; // eslint-disable-line

    return acc;
  }, {});
  const initial = initialMapping && Object.entries(initialMapping)
    .filter(([ , value ]) => headers.includes(value))
    .reduce((acc, entry) => {
      const [ key, value ] = entry;
      acc[key]             = value; // eslint-disable-line
      return acc;
    }, {});

  return { ...guessed, ...initial };
}

function getSummary(customers) {
  const missingInfo = customers.filter(customer => !hasValidContact(customer) || !hasValidName(customer)).length;
  const ready       = customers.length - missingInfo;

  return {
    missingInfo,
    ready
  };
}

export default connect(
  (state, { businessID }) => ({
    businessName:   Business.getBusinessName(state, businessID),
    initialMapping: Business.getCSVMapping(state, businessID),
    isManager:      User.isManager(state, businessID),
    provider:       Business.getCurrentProvider(state, businessID)
  }),
  (dispatch, { businessID }) => ({
    addMany:          customers => dispatch(addManyContacts({ businessID, customers })),
    updateCSVMapping: csvMapping => dispatch(Business.updateBusinessCSVMapping({ businessID, csvMapping }))
  })
)(restrictAccess(AddFilePage, ({ isManager }) => isManager));
